import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeft, UserPlus, MagnifyingGlass, ChatCircle, CreditCard, Star } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';

export default function HowItWorksPage() {
  const steps = [
    {
      number: 1,
      icon: UserPlus,
      title: 'Crie a sua Conta',
      description: 'Registe-se gratuitamente em menos de 1 minuto. Basta um email para começar.',
      color: 'bg-blue-500'
    },
    {
      number: 2,
      icon: MagnifyingGlass,
      title: 'Encontre o Prestador Ideal',
      description: 'Pesquise por localização, tipo de serviço, preço e avaliações. Vejá fotos e perfis detalhados.',
      color: 'bg-green-500'
    },
    {
      number: 3,
      icon: ChatCircle,
      title: 'Entre em Contacto',
      description: 'Envie uma mensagem para discutir detalhes, conhecer o prestador e combinar o serviço.',
      color: 'bg-purple-500'
    },
    {
      number: 4,
      icon: CreditCard,
      title: 'Confirme e Pague',
      description: 'Confirme o pedido e pague de forma segura. O prestador recebe a notificação.',
      color: 'bg-orange-500'
    },
    {
      number: 5,
      icon: Star,
      title: 'Avalie a Experiência',
      description: 'Após o serviço, deixe uma avaliação para ajudar outros donos de animais.',
      color: 'bg-yellow-500'
    }
  ];

  const clientSteps = [
    'Crie uma conta gratuita',
    'Adicione os seus animais ao perfil',
    'Pesquise prestadores na sua zona',
    'Compare perfis, preços e avaliações',
    'Envie um pedido de serviço',
    'Converse com o prestador pelo chat',
    'Confirme e pague o serviço',
    'Deixe uma avaliação'
  ];

  const providerSteps = [
    'Crie uma conta gratuita',
    'Complete o seu perfil de prestador',
    'Adicione fotos e defina os seus serviços',
    'Defina preços e zonas de atuação',
    'Verifique a sua identidade (opcional)',
    'Receba pedidos de clientes',
    'Aceite ou recuse pedidos',
    'Realize o serviço e receba o pagamento'
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl font-bold font-outfit mb-4">Como Funciona</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Encontrar o pet sitter perfeito nunca foi tão fácil
          </p>

          {/* Main Steps */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-8 text-center">5 Passos Simples</h2>
            <div className="space-y-6">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className={`w-12 h-12 rounded-full ${step.color} text-white flex items-center justify-center flex-shrink-0 font-bold text-lg`}>
                      {step.number}
                    </div>
                    <div className="flex-1 bg-card rounded-xl p-4 border">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon weight="duotone" className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">{step.title}</h3>
                      </div>
                      <p className="text-muted-foreground text-sm">{step.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* For Clients */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">Para Donos de Animais</h2>
            <div className="bg-primary/5 rounded-2xl p-6 border border-primary/10">
              <p className="text-muted-foreground mb-4">
                Encontrar um cuidador de confiança para o seu pet é fácil e <strong>100% gratuito</strong> para clientes:
              </p>
              <ol className="list-decimal pl-6 space-y-2">
                {clientSteps.map((step) => (
                  <li key={step} className="text-muted-foreground">{step}</li>
                ))}
              </ol>
            </div>
          </section>

          {/* For Providers */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">Para Prestadores</h2>
            <div className="bg-secondary/10 rounded-2xl p-6 border border-secondary/20">
              <p className="text-muted-foreground mb-4">
                Quer ganhar dinheiro a cuidar de animais? Comece hoje mesmo:
              </p>
              <ol className="list-decimal pl-6 space-y-2 mb-6">
                {providerSteps.map((step) => (
                  <li key={step} className="text-muted-foreground">{step}</li>
                ))}
              </ol>
              <div className="bg-white dark:bg-card rounded-lg p-4">
                <p className="font-medium mb-2">Planos para Prestadores:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Gratuito:</strong> Até 2 contactos por mês</li>
                  <li>• <strong>Pro (4,99€/mês):</strong> Contactos ilimitados + destaque</li>
                  <li>• <strong>Pay-per-Contacto (4€):</strong> Pague apenas quando receber</li>
                </ul>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-primary to-secondary text-white rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-semibold mb-4">Pronto para Começar?</h2>
            <p className="mb-6 opacity-90">
              Junte-se a milhares de donos de animais e prestadores em Portugal.
            </p>
            <Link to="/login">
              <Button variant="secondary" className="rounded-full" size="lg">
                Criar Conta Grátis
              </Button>
            </Link>
          </section>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
